import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, ArrowRight, Flame, Droplets, Zap, Refrigerator,
  Cloud, Lock, MoreHorizontal, X, Camera, AlertTriangle,
  CheckCircle, Loader2, Home, ImageIcon
} from "lucide-react";
import { API } from "@/App";

const issueTypes = [
  { id: "Heating", icon: Flame, label: "Heating", description: "Boiler, radiators, hot water" },
  { id: "Plumbing", icon: Droplets, label: "Plumbing", description: "Leaks, blocked drains, taps" },
  { id: "Electrical", icon: Zap, label: "Electrical", description: "Lights, sockets, switches" },
  { id: "Appliance", icon: Refrigerator, label: "Appliance", description: "Oven, fridge, washing machine" },
  { id: "Damp", icon: Cloud, label: "Damp & Mould", description: "Moisture, condensation, mould" },
  { id: "Locks", icon: Lock, label: "Locks & Security", description: "Door locks, windows, keys" },
  { id: "Other", icon: MoreHorizontal, label: "Other", description: "Something else" }
];

const severityOptions = [
  { id: "low", label: "Low", description: "Minor inconvenience, can wait", color: "bg-slate-100 text-slate-700" },
  { id: "medium", label: "Medium", description: "Affecting daily life", color: "bg-amber-100 text-amber-700" },
  { id: "high", label: "High", description: "Urgent, needs quick attention", color: "bg-orange-100 text-orange-700" },
  { id: "emergency", label: "Emergency", description: "Safety risk, immediate action", color: "bg-red-100 text-red-700" }
];

export const ReportIssuePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const token = localStorage.getItem("token");

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingProperty, setLoadingProperty] = useState(true);
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [formData, setFormData] = useState({
    issue_type: "",
    property_address: "",
    property_postcode: "",
    description: "",
    severity: "medium",
    is_emergency: false,
    media_urls: []
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  // Fetch tenant's property on mount
  useEffect(() => {
    fetchTenantProperty();
  }, []);

  const fetchTenantProperty = async () => {
    try {
      // Get current user info which includes property details
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const user = response.data;
      
      // If tenant has linked property, fetch it
      if (user.property_id) {
        try {
          const propResponse = await axios.get(`${API}/lettings/properties/${user.property_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const property = propResponse.data;
          setPropertyInfo(property);
          setFormData(prev => ({
            ...prev,
            property_address: property.address || '',
            property_postcode: property.postcode || ''
          }));
        } catch {
          // Property endpoint may not exist, try to use user's stored property info
          if (user.property_address) {
            setPropertyInfo({ address: user.property_address, postcode: user.property_postcode });
            setFormData(prev => ({
              ...prev,
              property_address: user.property_address || '',
              property_postcode: user.property_postcode || ''
            }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching property:", error);
    } finally {
      setLoadingProperty(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
        toast.error("Only images and videos are supported");
        continue;
      }

      setUploadingFile(true);
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      try {
        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });

        setUploadedFiles(prev => [...prev, {
          name: file.name,
          url: response.data.file_url,
          preview: URL.createObjectURL(file)
        }]);

        setFormData(prev => ({
          ...prev,
          media_urls: [...prev.media_urls, response.data.file_url]
        }));
        
        toast.success("Photo uploaded!");
      } catch (error) {
        toast.error("Failed to upload file");
      } finally {
        setUploadingFile(false);
      }
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      media_urls: prev.media_urls.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.issue_type) {
      toast.error("Please select an issue type");
      return;
    }

    if (uploadedFiles.length === 0) {
      toast.error("Please upload at least one photo of the issue");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/issues`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Issue reported successfully!");
      navigate(`/tenant/issue/${response.data.issue_id}/chat`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to report issue");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return !!formData.issue_type;
      case 2: return formData.description.length >= 10 && uploadedFiles.length > 0;
      case 3: return !!formData.severity;
      default: return true;
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h2 className="text-2xl font-semibold font-outfit mb-2">What type of issue?</h2>
              <p className="text-muted-foreground">Select the category that best describes the problem</p>
            </div>

            {/* Property info banner */}
            {propertyInfo && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <Home className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">{propertyInfo.address}</p>
                    <p className="text-sm text-blue-700">{propertyInfo.postcode}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-3">
              {issueTypes.map((type) => {
                const Icon = type.icon;
                const isSelected = formData.issue_type === type.id;

                return (
                  <Card
                    key={type.id}
                    data-testid={`issue-type-${type.id.toLowerCase()}`}
                    className={`cursor-pointer transition-all ${isSelected ? "border-primary ring-2 ring-primary/20 bg-primary/5" : "hover:border-slate-300"}`}
                    onClick={() => setFormData({ ...formData, issue_type: type.id })}
                  >
                    <CardContent className="p-4 text-center">
                      <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isSelected ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="font-medium mb-1">{type.label}</h3>
                      <p className="text-xs text-muted-foreground">{type.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h2 className="text-2xl font-semibold font-outfit mb-2">Describe the issue</h2>
              <p className="text-muted-foreground">Tell us what's happening and add photos</p>
            </div>

            {/* Property info (read-only) */}
            {propertyInfo && (
              <Card className="bg-slate-50">
                <CardContent className="p-4">
                  <Label className="text-sm text-muted-foreground">Property</Label>
                  <p className="font-medium">{propertyInfo.address}, {propertyInfo.postcode}</p>
                </CardContent>
              </Card>
            )}

            <Textarea
              data-testid="issue-description"
              placeholder="E.g., The boiler isn't heating the water. The pressure gauge shows 0.3 bar. I've tried pressing the reset button but it didn't help..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="min-h-[120px] text-base"
            />

            {/* Photo upload - REQUIRED */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-medium flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Photos of the issue
                  <span className="text-red-500">*</span>
                </Label>
                <span className="text-sm text-muted-foreground">Required</span>
              </div>
              
              {uploadedFiles.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-amber-800 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    Please upload at least one photo to help us understand the issue
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="relative w-24 h-24 rounded-lg overflow-hidden bg-slate-100 border-2 border-green-500">
                    <img src={file.preview} alt={file.name} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeFile(index)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-green-500 text-white text-xs py-0.5 text-center">
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      Uploaded
                    </div>
                  </div>
                ))}
                <button
                  data-testid="upload-media-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile}
                  className={`w-24 h-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-colors ${
                    uploadedFiles.length === 0 
                      ? "border-amber-400 bg-amber-50 text-amber-600 hover:border-amber-500" 
                      : "border-slate-200 text-slate-400 hover:border-primary hover:text-primary"
                  }`}
                >
                  {uploadingFile ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <Camera className="w-6 h-6 mb-1" />
                      <span className="text-xs font-medium">Add Photo</span>
                    </>
                  )}
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={handleFileUpload}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Take clear photos showing the problem. Multiple angles help!
              </p>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h2 className="text-2xl font-semibold font-outfit mb-2">How urgent is this?</h2>
              <p className="text-muted-foreground">This helps us prioritize your request</p>
            </div>

            <div className="space-y-3">
              {severityOptions.map((option) => (
                <Card
                  key={option.id}
                  data-testid={`severity-${option.id}`}
                  className={`cursor-pointer transition-all ${formData.severity === option.id ? "border-primary ring-2 ring-blue-100" : "hover:border-slate-300"}`}
                  onClick={() => setFormData({ ...formData, severity: option.id })}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.severity === option.id ? "border-primary bg-primary" : "border-slate-300"}`}>
                      {formData.severity === option.id && (
                        <CheckCircle className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${option.color}`}>
                          {option.label}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            key="step4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center">
              <h2 className="text-2xl font-semibold font-outfit mb-2">Review & Submit</h2>
              <p className="text-muted-foreground">Please confirm the details are correct</p>
            </div>

            {/* Emergency check */}
            <Card className={`transition-all ${formData.is_emergency ? "border-red-300 bg-red-50" : ""}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${formData.is_emergency ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-600"}`}>
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="emergency" className="text-base font-medium">
                        Is this an emergency?
                      </Label>
                      <Switch
                        id="emergency"
                        data-testid="emergency-toggle"
                        checked={formData.is_emergency}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_emergency: checked })}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Gas leaks, flooding, no heating, security issues
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {formData.is_emergency && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Emergency guidance:</p>
                    <ul className="text-sm text-red-700 mt-1 space-y-1">
                      <li>• Gas leak? Leave immediately, call 0800 111 999</li>
                      <li>• Fire? Evacuate and call 999</li>
                      <li>• Stay safe first, then report</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Summary */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium mb-4">Issue Summary</h3>
                <div className="space-y-3">
                  {propertyInfo && (
                    <div className="flex justify-between items-start">
                      <span className="text-muted-foreground">Property:</span>
                      <span className="font-medium text-right">{propertyInfo.address}<br/><span className="text-sm text-muted-foreground">{propertyInfo.postcode}</span></span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Issue type:</span>
                    <span className="font-medium">{formData.issue_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Severity:</span>
                    <span className="font-medium capitalize">{formData.severity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Photos:</span>
                    <span className="font-medium text-green-600">{uploadedFiles.length} uploaded ✓</span>
                  </div>
                  <div className="pt-2 border-t">
                    <span className="text-muted-foreground text-sm">Description:</span>
                    <p className="text-sm mt-1">{formData.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (loadingProperty) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => step > 1 ? setStep(step - 1) : navigate("/tenant")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <span className="text-sm text-muted-foreground">Step {step} of {totalSteps}</span>
            <div className="w-10" />
          </div>
          <Progress value={progress} className="h-1" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-8 pb-32">
        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4">
        <div className="max-w-md mx-auto">
          {step < totalSteps ? (
            <Button
              data-testid="next-step-btn"
              className="w-full h-12 rounded-xl text-base"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
            >
              {step === 2 && uploadedFiles.length === 0 ? (
                "Add Photos to Continue"
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              data-testid="submit-issue-btn"
              className="w-full h-12 rounded-xl text-base"
              onClick={handleSubmit}
              disabled={loading || uploadedFiles.length === 0}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Issue"
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
